// axiosInstance.js
import axios from 'axios';
import { notifyOverviewMutation } from '../pages/allocate/overview/overviewEvents';

const normalizeBaseUrl = (url) => url.replace(/\/+$/, '');

const base_url = normalizeBaseUrl(
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'
);

const PUBLIC_AUTH_PATHS = new Set([
  '/users/login/',
  '/users/email-sign-in/',
  '/users/register/',
  '/users/request-reset-password/',
  '/users/refresh/',
]);

const PUBLIC_AUTH_PATH_PREFIXES = ['/users/reset-password/', '/mobile/links/'];

const getRequestPath = (config = {}) => {
  try {
    const parsedUrl = new URL(config.url || '', config.baseURL || base_url);
    return parsedUrl.pathname;
  } catch (error) {
    return (config.url || '').split('?')[0];
  }
};

const isPublicAuthRequest = (config = {}) => {
  const path = getRequestPath(config);
  return (
    PUBLIC_AUTH_PATHS.has(path) ||
    PUBLIC_AUTH_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))
  );
};

// Keep staging/prod env vars safe whether they end with /api or /api/.
const api = axios.create({
  baseURL: base_url,
  timeout: 600000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const TOKEN_REJECTION_STATUSES = new Set([400, 401]);

let refreshPromise = null;

const clearTokensAndRedirectToLogin = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const refreshAccessToken = (refreshToken) => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${base_url}/users/refresh/`, {
        refresh: refreshToken,
      })
      .then((response) => {
        localStorage.setItem('accessToken', response.data.access);
        // Field staff sessions renew their refresh token on every refresh.
        if (response.data.refresh) {
          localStorage.setItem('refreshToken', response.data.refresh);
        }
        return response.data.access;
      })
      .catch((error) => {
        if (
          !axios.isCancel(error) &&
          TOKEN_REJECTION_STATUSES.has(error.response?.status)
        ) {
          clearTokensAndRedirectToLogin();
        }
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

api.interceptors.request.use(
  (request) => {
    if (isPublicAuthRequest(request)) {
      if (request.headers) {
        delete request.headers.Authorization;
        delete request.headers.authorization;
      }
      return request;
    }

    const token = localStorage.getItem('accessToken');
    if (token) {
      request.headers['Authorization'] = `Bearer ${token}`;
    }
    return request;
  },
  (error) => {
    console.error('Request Error', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    if (response.status >= 200 && response.status < 300) notifyOverviewMutation(response.config);
    return response;
  },
  async (error) => {
    if (axios.isCancel(error) || !error.response) {
      return Promise.reject(error);
    }

    const originalRequest = error.config || {};

    // Check if the error is 401 and if we haven't already retried the request
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isPublicAuthRequest(originalRequest)
    ) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        clearTokensAndRedirectToLogin();
        return Promise.reject(error);
      }

      try {
        const accessToken = await refreshAccessToken(refreshToken);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
